import { gettext } from '@eventbrite/i18n';
import { formatMajorMoney } from '@eventbrite/intl';
import simmut from 'simmut';

/*

Display logic

We have two types of events: tiered events (new create) and non tiered events (old create)

In newer events, we have one ticket class per variant and tier
In older events, we have an array of variants nested inside of the ticket class

We have then the following fields

FIELDS RELATED TO HOW WE DISPLAY THINGS

ticketClass.name
ticketClass.display_name

ticketClass.variants[0].name --> string
ticketClass.variants[0].display_name --> string
ticketClass.variants[0].isPrimary --> boolean

ticketClass.variants[n>0].code --> string
ticketClass.variants[n>0].name --> string

Notes:

In new create, we have exactly one variant per ticketClass, but the field isPrimary is not there
In old create, can have more than one variant per ticketClass

RELEVANT FIELDS FOR PRICING

ticketClass.variants[n].cost --> structure
ticketClass.variants[n].total_cost --> structure
ticketClass.variants[n].use_all_in_price -->boolean


TICKET NAME DISPLAY

Due to how heterogeneous the data is, the algorithm is as follow

When we have only one variant, we use
  variants[0].display_name, fallback to ticketClass.display_name, fallback to ticketClass.name

Wen we have more than one variant
  If it is the primary variant, we use variant.display_name+ (Full Price)
  If it is not the primary variant, we use ticketClass.display_name + (variant.code)


PRICE DISPLAY

If use_all_in_price, we display total_cost. Otherwise, we display cost


PAYMENT CONSTRAINTS

ticketClass.conflictingPayment --> boolean

ticketClass.payment_constraints --> array
ticketClass.payment_constraints[n>0] --> structure

ticketClass.payment_constraints[n>0].id --> string
ticketClass.payment_constraints[n>0].instrument_type --> string
ticketClass.payment_constraints[n>0].payment_method --> string

We will be displaying icons of constrained payment methods between ticket name display and ticket price dispaly
When selecting a ticket with payment constraints, we will need to set ticketClass.conflictingPayment
to true for other tickets with payment constraints that have none in common
  Ex: When selecting a Visa only ticket, an Amex only ticket should have conflictingPayment set to true

*/

const { get } = simmut;

const getVariantsFromTier = ({ globals = {}, tier }) => {
    const { ticketClasses, complimentaryOrder } = globals;

    if (!ticketClasses) {
        return [];
    }
    const variants = Object.keys(ticketClasses).reduce((list, index) => {
        const ticketClass = ticketClasses[index];

        const ticketClassTier = get(ticketClass, 'reserved_seating.tier');

        if (ticketClassTier && ticketClassTier === tier) {
            const displayName = get(ticketClass, 'display_name');
            const nameFromClass = get(ticketClass, 'name');
            const ticketName = displayName || nameFromClass;
            const variantId = get(ticketClass, 'variant_id');
            const displayPrice = get(ticketClass, 'cost.display');
            const salesStart = get(ticketClass, 'sales_start');
            const salesEnd = get(ticketClass, 'sales_end');
            const paymentConstraints = get(ticketClass, 'payment_constraints');
            const conflictingPayment =
                get(ticketClass, 'conflictingPayment') || false;
            let instrumentType = null;
            let paymentMethods = null;

            if (
                Array.isArray(paymentConstraints) &&
                paymentConstraints.length
            ) {
                paymentMethods = paymentConstraints.map((constraint) => {
                    if (!instrumentType) {
                        instrumentType = constraint['instrument_type'];
                    }

                    return constraint['payment_method'] || instrumentType;
                });
            }

            const now = Date.now();

            if (salesStart) {
                const dStart = new Date(salesStart);

                if (now < dStart) {
                    return list;
                }
            }
            if (salesEnd) {
                const dEnd = new Date(salesEnd);

                if (now > dEnd) {
                    return list;
                }
            }

            const variantsNode = ticketClass.variants;

            if (variantsNode && variantsNode.length) {
                const subVariants = [];

                Object.keys(variantsNode).forEach((childName) => {
                    const variant = variantsNode[childName];
                    const hasMultipleSubVariants = variantsNode.length > 1;
                    const primary = hasMultipleSubVariants
                        ? variant.primary
                        : true;
                    const useAllInPrice = variant['use_all_in_price'];
                    const priceNode = useAllInPrice
                        ? variant['total_cost']
                        : variant.cost;
                    let subVariantId;
                    let variantPrice = '';
                    let nameToDisplay;

                    if (priceNode) {
                        const currency = priceNode.currency;
                        const value = +priceNode.value;

                        variantPrice = formatMajorMoney(value, currency);
                    }

                    if (primary) {
                        if (hasMultipleSubVariants) {
                            nameToDisplay = `${
                                variant['display_name']
                            } (${gettext('Full Price')})`;
                        } else {
                            nameToDisplay = nameFromClass;
                        }
                        subVariantId = variantId;
                    } else {
                        nameToDisplay = `${ticketName} (${variant.code})`;
                        subVariantId = variant.id;
                    }
                    subVariants.push({
                        price: variantPrice || gettext('Free'),
                        ticketName: nameToDisplay,
                        variantId: subVariantId,
                        instrumentType,
                        paymentMethods,
                        conflictingPayment,
                        complimentaryOrder,
                    });
                });
                return [...list, ...subVariants];
            }
            return [
                ...list,
                {
                    price: displayPrice || gettext('Free'),
                    complimentaryOrder,
                    ticketName,
                    variantId,
                },
            ];
        }

        return list;
    }, []);

    return variants;
};

const getTicketClassFromVariantId = ({ ticketClasses, variantId }) => {
    const result = null;

    if (ticketClasses) {
        const ticketClassesLength = ticketClasses.length;

        for (let i = 0; i < ticketClassesLength; i++) {
            const ticketVariantId = get(ticketClasses[i], 'variant_id');

            if (ticketVariantId === variantId) {
                const ticketClassNode = ticketClasses[i];

                return ticketClassNode;
            }
        }
    }

    return result;
};

const getTicketDataFromVariantId = ({ ticketClasses, variantId }) => {
    const ticketClassNode = getTicketClassFromVariantId({
        ticketClasses,
        variantId,
    });

    if (ticketClassNode) {
        const maximumQuantity = ticketClassNode['maximum_quantity_per_order'];
        const maximumQuantityWithoutPending =
            ticketClassNode['maximum_quantity_per_order_without_pending'];
        const ticketName = ticketClassNode.name;

        return {
            maximumQuantity,
            maximumQuantityWithoutPending,
            ticketName,
        };
    }
    return {};
};

export default {
    getTicketClassFromVariantId,
    getTicketDataFromVariantId,
    getVariantsFromTier,
};
